import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createFirm, getAllFirmNames, updateFirm } from "../../../Services/Firm";
import {
  buildFirmNameIndex,
  getFirmDuplicateFieldErrors,
} from "../../../utils/firmNameUniqueness";
import { createAddress } from "../../../Services/Address";
import { createOffice } from "../../../Services/Office";
import { manageFirmSlice } from "../../../redux";
import { resolveCountryRegionNames } from "../../../utils/resolveCountryRegionNames";
import { toastError, toastPromise } from "../../../utils/toast";
import type IFirm from "../../../interfaces/models/IFirm";
import type { IOfficeNew } from "../../../interfaces/models/IOffice";
import {
  buildAddressPayload,
  buildFirmPayload,
  buildOfficePayload,
} from "./buildCreateFirmPayloads";
import type { CreateFirmFormState } from "./createFirmFormTypes";
import {
  hasCreateFirmFormErrors,
  validateCreateFirmForm,
} from "./validateCreateFirmForm";
import { uploadFirmLogo } from "./uploadFirmLogos";

type CreatedFirmRef = {
  id: string;
  firm: IFirm;
};

export function useCreateFirmWithOffice(regionCatalog: { id: string; name: string }[]) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<
    ReturnType<typeof validateCreateFirmForm>
  >({});
  const [createdFirmRef, setCreatedFirmRef] = useState<CreatedFirmRef | null>(
    null,
  );
  const [partialError, setPartialError] = useState<string | null>(null);

  const resolveRegionNames = useCallback(
    (country?: {
      regions?: unknown;
      regionNames?: string | string[] | null;
    }) => resolveCountryRegionNames(country, regionCatalog),
    [regionCatalog],
  );

  const submit = useCallback(
    async (
      form: CreateFirmFormState,
      selectedCountry?: {
        regions?: unknown;
        regionNames?: string | string[] | null;
      },
      catalog?: {
        countryHasStates?: boolean;
        stateHasCities?: boolean;
      },
    ) => {
      const existingFirms = await getAllFirmNames();
      const firmNameIndex = buildFirmNameIndex(existingFirms);
      const errors = validateCreateFirmForm(form, {
        firmNameIndex,
        countryHasStates: catalog?.countryHasStates,
        stateHasCities: catalog?.stateHasCities,
      });
      setFieldErrors(errors);
      if (hasCreateFirmFormErrors(errors)) {
        toastError("Please correct the highlighted fields.");
        return;
      }

      if (!form.serviceType) {
        toastError("Service type is required.");
        return;
      }

      const serviceType = form.serviceType;

      setSaving(true);
      setPartialError(null);

      const regionNames = resolveRegionNames(selectedCountry);

      try {
        await toastPromise(
          (async () => {
            let firmRecord = createdFirmRef?.firm;
            let firmId = createdFirmRef?.id ?? "";

            if (!firmId) {
              const firmPayload = buildFirmPayload(form, regionNames);
              const created = (await createFirm(
                firmPayload as unknown as IFirm,
              )) as IFirm;
              firmId = String(created.id ?? "");
              if (!firmId) {
                throw new Error("Firm was created but no id was returned.");
              }
              firmRecord = created;
              setCreatedFirmRef({ id: firmId, firm: created });
            }

            const logoUrl = await uploadFirmLogo(firmId, form.logo);

            const firmPayload = buildFirmPayload(form, regionNames);
            const updatedFirm = (await updateFirm({
              ...(firmRecord ?? {}),
              ...firmPayload,
              id: firmId,
              logo: logoUrl,
            } as unknown as IFirm)) as IFirm;
            firmRecord = updatedFirm;
            setCreatedFirmRef({ id: firmId, firm: updatedFirm });

            const addressPayload = buildAddressPayload(form, regionNames);
            const createdAddress = await createAddress(
              addressPayload as Parameters<typeof createAddress>[0],
            );
            const addressId = String(createdAddress?.id ?? "");
            if (!addressId) {
              throw new Error("Address was created but no id was returned.");
            }

            const officePayload = buildOfficePayload(
              form,
              firmId,
              form.firmName.trim(),
              addressId,
              regionNames,
              serviceType,
            );
            const createdOffice = (await createOffice(
              officePayload,
            )) as IOfficeNew;

            if (!createdOffice?.id) {
              throw new Error("Office was created but no id was returned.");
            }

            if (firmRecord) {
              dispatch(manageFirmSlice.actions.SetFirm(firmRecord));
            }

            setCreatedFirmRef(null);
            navigate("/ManageFirm");

            return { firmId, officeId: createdOffice.id };
          })(),
          {
            pending: createdFirmRef
              ? "Completing office setup..."
              : "Creating firm and main office...",
            success: "Firm and main office created successfully.",
          },
        );
      } catch (error) {
        const duplicateErrors = getFirmDuplicateFieldErrors(error);
        if (Object.keys(duplicateErrors).length > 0) {
          setFieldErrors((prev) => ({ ...prev, ...duplicateErrors }));
        }

        const message =
          error instanceof Error
            ? error.message
            : "Unable to complete firm creation.";
        if (createdFirmRef) {
          setPartialError(
            `Firm was created (ID: ${createdFirmRef.id}). Fix the remaining fields and save again to create the main office without duplicating the firm.`,
          );
        }
        toastError(message);
      } finally {
        setSaving(false);
      }
    },
    [createdFirmRef, dispatch, navigate, resolveRegionNames],
  );

  return {
    saving,
    fieldErrors,
    partialError,
    createdFirmId: createdFirmRef?.id ?? null,
    submit,
  };
}
